import {
  distributedCategoricalColor,
  eventDefaultColor,
  paletteFor,
  priorityColor,
  resolveThemeColors,
  topicColorResolved,
  type Calendar,
  type Label,
  type RoleName,
  type ThemeConfig,
  type ThemeMode,
  type Topic,
} from "@app/core";

import { CAL_PALETTE_OFFSET } from "../../../features/entity-colors.js";
import type { BoardTargetRef, EntityTarget } from "../../../stores/index.js";

/** Humanize a Material role identifier, e.g. "onPrimaryContainer" → "On primary container". */
export function humanizeRole(role: string): string {
  const spaced = role.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The color a board target currently resolves to — mirrors what its row on the
 *  theme settings page displays, so the board's selection ring matches the UI. */
export function boardTargetHex(
  ref: BoardTargetRef,
  config: ThemeConfig,
  topics: readonly Topic[],
  calendars: readonly Calendar[],
  labels: readonly Label[] = [],
): string {
  switch (ref.kind) {
    case "entity": return entityTargetHex(ref.target, config, topics, calendars);
    case "label": return labels.find((l) => l.id === ref.id)?.color ?? "#888888";
    case "paletteSlot": return paletteFor(config)[ref.index] ?? "#888888";
    case "role":
      return config.roleOverrides[ref.mode][ref.role]
        ?? resolveThemeColors({ ...config, roleOverrides: { light: {}, dark: {} } }, ref.mode)[ref.role];
    case "source": return ref.mode === "dark" ? config.sourceDark ?? config.source : config.source;
  }
}

function entityTargetHex(
  target: EntityTarget,
  config: ThemeConfig,
  topics: readonly Topic[],
  calendars: readonly Calendar[],
): string {
  const entity = config.entity;
  switch (target.kind) {
    case "global": return entity.global ?? "#888888";
    case "taskKind":
      return (target.isEvent ? entity.kind?.event : entity.kind?.task)
        ?? entity.global
        ?? (target.isEvent ? eventDefaultColor(config) : "#888888");
    case "priority": return priorityColor(config, target.priority) || "#888888";
    case "calendar": {
      const override = entity.calendars?.[target.id];
      if (override) return override;
      const index = calendars.findIndex((calendar) => calendar.id === target.id);
      return config.autoColorCalendars && index >= 0
        ? distributedCategoricalColor(config, CAL_PALETTE_OFFSET + index)
        : "#888888";
    }
    case "topic": {
      const index = topics.findIndex((topic) => topic.id === target.id);
      return topicColorResolved(config, target.id, Math.max(0, index));
    }
  }
}

/** The theme-store actions a board pick can dispatch to (structural subset of
 *  the store so tests can stub it). */
export interface BoardTargetActions {
  setEntityColor: (target: EntityTarget, hex: string | null) => void;
  setLabelColor: (id: string, hex: string | null) => void;
  setPaletteSlot: (index: number, hex: string | null) => void;
  setRoleOverride: (mode: ThemeMode, role: RoleName, hex: string | null) => void;
  setSource: (hex: string, mode?: ThemeMode) => void;
}

/** Apply a board-picked color to whatever target is armed. */
export function applyBoardTarget(actions: BoardTargetActions, ref: BoardTargetRef, hex: string): void {
  switch (ref.kind) {
    case "entity": actions.setEntityColor(ref.target, hex); break;
    case "label": actions.setLabelColor(ref.id, hex); break;
    case "paletteSlot": actions.setPaletteSlot(ref.index, hex); break;
    case "role": actions.setRoleOverride(ref.mode, ref.role, hex); break;
    case "source": actions.setSource(hex, ref.mode); break;
  }
}

/** Display name of a board target for the board header. Names resolve through
 *  the passed lookups; i18n strings come pre-translated from the caller. */
export function boardTargetLabel(
  ref: BoardTargetRef,
  ctx: {
    topics: readonly Topic[];
    calendars: readonly Calendar[];
    labels?: readonly Label[];
    strings: {
      global: string; task: string; event: string;
      slot: (n: number) => string; sourceLight: string; sourceDark: string;
      modeLight: string; modeDark: string;
    };
  },
): string {
  const { strings } = ctx;
  switch (ref.kind) {
    case "entity": {
      const target = ref.target;
      switch (target.kind) {
        case "global": return strings.global;
        case "taskKind": return target.isEvent ? strings.event : strings.task;
        case "priority": return target.priority;
        case "calendar": return ctx.calendars.find((c) => c.id === target.id)?.name ?? "?";
        case "topic": return ctx.topics.find((t) => t.id === target.id)?.name ?? "?";
      }
      break;
    }
    case "label": return ctx.labels?.find((l) => l.id === ref.id)?.name ?? "?";
    case "paletteSlot": return strings.slot(ref.index + 1);
    case "role": return `${humanizeRole(ref.role)} · ${ref.mode === "dark" ? strings.modeDark : strings.modeLight}`;
    case "source": return ref.mode === "dark" ? strings.sourceDark : strings.sourceLight;
  }
  return "?";
}
